/* eslint-disable @typescript-eslint/no-explicit-any */
import { i18n } from '@/plugins/i18n'
import { libelleDynamique } from '@/utils/libelleDynamique'
import { LngLat, LngLatBounds, type LngLatLike } from 'maplibre-gl'

export const normalizeUrl = (url: string) => {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  return `https://${url}`
}

export const formatHTMLForSheetView = (rawHTML: string): string => {
  if (!rawHTML) return ''
  if (rawHTML === '<p></p>') return ''
  return rawHTML.replace(/<p><\/p>/g, '<br />')
}

export const uniqueArray = (array: any[], key = 'id') => {
  return array.filter((obj1, i, arr) => arr.findIndex((obj2) => obj2[key] === obj1[key]) === i)
}

export const reduceText = (text: string, length: number) => {
  if (text.length > length && text != null) {
    return text.slice(0, length) + '...'
  }
  return text
}

const normalizeForIndex = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2019\u2018]/g, "'")
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

/**
 * Libellés affichés pour les catégories de l'atlas et les sous-domaines.
 *
 * Ces intitulés sont déjà indexés côté front : la base sert un libellé long, et l'interface en
 * affiche une forme courte, plus lisible dans le panneau latéral. Les catégories de l'atlas
 * (geodata.atlas_category) n'ont en outre pas de colonne anglaise en base, contrairement aux
 * domaines et sous-domaines du référentiel : leur traduction se fait donc ici, dans le même index.
 *
 * Chaque entrée porte les deux langues, et l'index est interrogé aussi bien par la forme française
 * que par l'anglaise : un libellé déjà traduit repasse ainsi sans dommage par ces fonctions, qui
 * sont appelées en cascade avec libelleDynamique.
 */
interface LibelleIndexe {
  fr: string
  en: string
}

const construireIndex = (entrees: LibelleIndexe[]): Record<string, LibelleIndexe> => {
  const index: Record<string, LibelleIndexe> = {}
  for (const entree of entrees) {
    index[normalizeForIndex(entree.fr)] = entree
    index[normalizeForIndex(entree.en)] = entree
  }
  return index
}

/** Catégories de l'atlas, servies en français par l'API et sans équivalent anglais en base. */
const ATLAS_CATEGORY_LIB_INDEX: Record<string, LibelleIndexe> = construireIndex([
  { fr: 'Educatif, Sportif et Culturel', en: 'Education, Sports and Culture' },
  { fr: 'Sanitaire et Social', en: 'Health and Social Affairs' },
  { fr: 'Economique', en: 'Economy' },
  { fr: 'Autres Données', en: 'Other data' },
  { fr: 'Population', en: 'Population' },
  { fr: 'Densité démographique', en: 'Population density' },
  { fr: 'Urbanisme', en: 'Town planning' },
  { fr: 'Réseau routier', en: 'Road network' },
])

/** Formes courtes affichées dans le panneau latéral, indexées par le libellé complet du back. */
const SOUS_DOMAINE_LIB_INDEX: Record<string, LibelleIndexe> = construireIndex([
  {
    fr: 'Education et formation professionnelle',
    en: 'Education and vocational training',
  },
  { fr: 'Sports et Loisirs', en: 'Sports and Leisure' },
  { fr: 'Santé et Social', en: 'Health and Social Affairs' },
  {
    fr: 'Aménagement du territoire et urbanisme',
    en: 'Regional planning and town planning',
  },
])

/**
 * Libellés complets servis par l'API qui mènent à une même forme courte. Ils ne peuvent pas vivre
 * dans l'index lui-même, qui n'y associerait qu'une seule des deux langues.
 */
const SOUS_DOMAINE_ALIAS: Record<string, string> = {
  [normalizeForIndex('Education, alphabetisation et formation professionnelle')]:
    'Education et formation professionnelle',
  [normalizeForIndex('Education, literacy and vocational training')]:
    'Education et formation professionnelle',
  [normalizeForIndex('Jeunesse, Sports et Loisirs')]: 'Sports et Loisirs',
  [normalizeForIndex('Youth, sports and leisure')]: 'Sports et Loisirs',
  [normalizeForIndex('Sante, Population et Action Sociale')]: 'Santé et Social',
  [normalizeForIndex('Health, population, and social action')]: 'Santé et Social',
  [normalizeForIndex('Planification, Amenagement du territoire, de l urbanisme et du Foncier')]:
    'Aménagement du territoire et urbanisme',
  [normalizeForIndex("Planification amenagement du territoire de l'urbanisme et de l'habitat")]:
    'Aménagement du territoire et urbanisme',
  [normalizeForIndex('Planning, development, urbanism and housing')]:
    'Aménagement du territoire et urbanisme',
}

/**
 * Libellé court d'un sous-domaine, dans la langue de l'interface. Un libellé inconnu de l'index est
 * rendu tel quel : il porte alors déjà sa traduction, venue de la base (voir libelleDynamique).
 */
export const formatSousDomaineLib = (lib: string | null | undefined): string => {
  if (!lib) return ''
  const normalise = normalizeForIndex(lib)
  const cle = normalizeForIndex(SOUS_DOMAINE_ALIAS[normalise] ?? lib)
  const indexe = SOUS_DOMAINE_LIB_INDEX[cle]
  return indexe ? libelleDynamique(indexe.fr, indexe.en) : lib
}

/**
 * Libellé d'une catégorie de l'atlas ou d'un domaine : le mot « Domaine » qui préfixe certains
 * libellés est retiré, puis l'index fournit la forme courte dans la langue de l'interface. Un
 * libellé absent de l'index est rendu tel quel — c'est le cas des domaines du référentiel, qui
 * portent leur propre traduction en base (voir libelleDynamique).
 */
export const formatDomaineLib = (lib: string | null | undefined): string => {
  if (!lib) return ''
  const sansPrefixe = lib.replace(/^domaine\s+/i, '').trim()
  const indexe = ATLAS_CATEGORY_LIB_INDEX[normalizeForIndex(sansPrefixe)]
  return indexe ? libelleDynamique(indexe.fr, indexe.en) : sansPrefixe
}

export const debounce = (func: any, wait = 500) => {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: any[]) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

export function getNestedObjectValue(obj: any, propStr = '') {
  const keys = propStr.split('.')
  return (
    keys.reduce((acc, key) => {
      if (!acc || !(key in acc)) return null
      return acc[key]
    }, obj) || null
  )
}

// duplicated: src/services/api/ApiPlatformService.ts:1
export function transformSymfonyRelationToIRIs<T>(entity: any): T {
  for (const key in entity) {
    if (Array.isArray(entity[key])) {
      entity[key] = transformSymfonyRelationToIRIs(entity[key])
    } else if (
      typeof entity[key] === 'object' &&
      !Array.isArray(entity[key]) &&
      entity[key] !== null &&
      '@id' in entity[key]
    ) {
      entity[key] = entity[key]['@id']
    }
  }
  return entity
}

export function localizeDate(
  date: string | Date,
  format: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' },
) {
  date = new Date(date)
  return date.toLocaleDateString('fr-FR', format)
}

export function localizeTime(
  date: string | Date,
  format: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' },
) {
  date = new Date(date)
  return date.toLocaleTimeString('fr-FR', format)
}

export function getDateRangeLabel(startAt: string | Date | null, endAt: string | Date | null) {
  if (!startAt) return ''
  startAt = new Date(startAt)
  startAt.setHours(0, 0, 0, 0)

  if (!endAt || isSameDay(startAt, endAt))
    return localizeDate(startAt, { year: 'numeric', month: 'long', day: 'numeric' })

  endAt = new Date(endAt)
  endAt.setHours(0, 0, 0, 0)

  const showStartAtMonth = startAt.getMonth() !== endAt.getMonth()
  const showStartAtYear = startAt.getFullYear() !== endAt.getFullYear()
  const localizedStartAt = localizeDate(startAt, {
    year: showStartAtYear ? 'numeric' : undefined,
    month: showStartAtMonth ? 'long' : undefined,
    day: 'numeric',
  })
  const localizedEndAt = localizeDate(endAt, { year: 'numeric', month: 'long', day: 'numeric' })
  return i18n.t('date.dateRangeLabel', { startAt: localizedStartAt, endAt: localizedEndAt })
}

export function getDateDiff(date1: string | Date, date2: string | Date): number {
  date1 = new Date(date1)
  date1.setHours(0, 0, 0, 0)
  date2 = new Date(date2)
  date2.setHours(0, 0, 0, 0)
  return date1.getTime() - date2.getTime()
}

export function isSameDay(date1: string | Date, date2: string | Date) {
  return getDateDiff(date1, date2) === 0
}

export function downloadJson(data: any, fileName: string) {
  if (!data.features || !data.features.length) return
  for (const feature of data.features) {
    feature.type = 'Feature'
  }
  const blob = new Blob([JSON.stringify(data)], { type: 'application/json' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${fileName}.geojson`
  link.click()
}

export async function fetchImageAsBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      return null
    }
    const blob = await response.blob()

    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Error fetching image as base64:', error)
    return null
  }
}

export function getBboxFromPointsGroup(pointsGroup: LngLatLike[]): LngLatBounds {
  if (pointsGroup.length === 0) {
    return new LngLatBounds()
  }
  let minLat = Infinity,
    maxLat = -Infinity
  let minLng = Infinity,
    maxLng = -Infinity

  for (const { lat, lng } of pointsGroup as LngLat[]) {
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
  }

  return new LngLatBounds({ lng: minLng, lat: minLat }, { lng: maxLng, lat: maxLat })
}
